// Youtube success stories grid
// limited by few then view more from youtube

import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Card, Grid, Link, Stack, Typography } from '@mui/material';
// components
import Iconify from '../../../components/iconify';
import { MotionInView, varFadeInUp } from '../../../components/animate';
import { SectionHeader, SectionShell } from '../common';
// hooks
import useLazyMockWeb from '../../../hooks/useLazyMockWeb';
// redux
import { useDispatch, useSelector } from '../../../redux/store';
import { getSuccessStories } from '../../../redux/slices/course';

import {
  YOUTUBE_CHANNEL_URL,
  shareLinksFor,
} from '../../../data/contact-info';
// ----------------------------------------------------------------------

const YOUTUBE_WATCH_URL = 'https://www.youtube.com/watch?v=';
const YOUTUBE_THUMB_URL = 'https://i.ytimg.com/vi/';

// A YouTube video id is exactly 11 URL-safe base64 characters.
const isVideoId = (videoId) => typeof videoId === 'string' && /^[A-Za-z0-9_-]{11}$/.test(videoId);

// Links to the video itself when the id is real, otherwise to the channel so a
// placeholder payload never produces a dead watch page.
function getWatchUrl(videoId) {
  return isVideoId(videoId) ? `${YOUTUBE_WATCH_URL}${videoId}` : YOUTUBE_CHANNEL_URL;
}

// The card thumbnail is always the video's own YouTube frame — no separate
// image asset. Only `hqdefault` is guaranteed to exist, so callers fall back
// to it when the high-resolution frame is missing.
const getThumbnailUrl = (videoId) => `${YOUTUBE_THUMB_URL}${videoId}/hqdefault.jpg`;

// ----------------------------------------------------------------------

StoryCard.propTypes = {
  story: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    student: PropTypes.string,
    videoId: PropTypes.string,
  }).isRequired,
  index: PropTypes.number,
};

function StoryCard({ story, index = 0 }) {
  const { title, student, videoId } = story;
  const hasVideo = isVideoId(videoId);
  const watchUrl = getWatchUrl(videoId);
  const shareLinks = shareLinksFor(watchUrl, `${title} — ${student}`);

  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }} sx={{ display: 'flex' }}>
      <MotionInView
        variants={varFadeInUp}
        transition={{ delay: Math.min(index, 5) * 0.06 }}
        sx={{ width: '100%' }}
      >
        <Card
          sx={{
            position: 'relative',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            overflow: 'hidden',
            transition: 'transform 0.4s cubic-bezier(0.43, 0.13, 0.23, 0.96), box-shadow 0.4s ease',
            '@media (hover: hover)': {
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: (theme) => `0 18px 36px ${alpha(theme.palette.grey[900], 0.18)}`,
              },
            },
          }}
        >
          <Box
            component="a"
            href={watchUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Play video: ${title}`}
            sx={{
              position: 'relative',
              display: 'block',
              aspectRatio: '16 / 9',
              overflow: 'hidden',
              bgcolor: 'grey.900',
              '& img': { transition: 'transform 0.5s cubic-bezier(0.43, 0.13, 0.23, 0.96)' },
              '@media (hover: hover)': {
                '&:hover img': { transform: 'scale(1.06)' },
                '&:hover .play-btn': { transform: 'scale(1.1)' },
              },
            }}
          >
            {hasVideo ? (
              <Box
                component="img"
                alt={title}
                src={getThumbnailUrl(videoId)}
                loading="lazy"
                decoding="async"
                sx={{
                  position: 'absolute',
                  inset: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'grid',
                  placeItems: 'center',
                  color: (theme) => alpha(theme.palette.common.white, 0.35),
                }}
              >
                <Iconify icon="ic:round-video-library" width={44} />
              </Box>
            )}

            <Box sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center' }}>
              <Box
                className="play-btn"
                sx={{
                  width: { xs: 54, md: 64 },
                  height: { xs: 38, md: 44 },
                  borderRadius: '12px',
                  bgcolor: '#FF0000',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.35)',
                  transition: 'transform 0.3s ease',
                }}
              >
                <Box component="svg" viewBox="0 0 24 24" sx={{ width: 22, height: 22, fill: '#FFFFFF' }}>
                  <path d="M8 5v14l11-7z" />
                </Box>
              </Box>
            </Box>
          </Box>

          <Stack sx={{ p: { xs: 1.75, sm: 2 }, flexGrow: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: 'text.primary',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              <Link
                href={watchUrl}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
                sx={{ color: 'inherit' }}
              >
                {title}
              </Link>
            </Typography>

            <Typography
              variant="caption"
              sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', mt: 'auto', pt: 1 }}
            >
              <Iconify icon="ic:round-person" width={14} sx={{ mr: 0.5, flexShrink: 0 }} />
              <Box
                component="span"
                sx={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {student}
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() => {
                  setShareOpen((open) => !open);
                  setCopied(false);
                }}
                aria-expanded={shareOpen}
                aria-label={`Share story: ${title}`}
                sx={{
                  ml: 'auto',
                  flexShrink: 0,
                  border: 0,
                  background: 'none',
                  cursor: 'pointer',
                  color: 'text.disabled',
                  display: 'inline-flex',
                  alignItems: 'center',
                  p: 0.5,
                  borderRadius: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': { color: 'primary.main' },
                }}
              >
                <Iconify icon={shareOpen ? 'eva:close-fill' : 'eva:share-outline'} width={16} />
              </Box>
            </Typography>

            {shareOpen && (
              <Stack direction="row" useFlexGap spacing={0.75} sx={{ pt: 1, flexWrap: 'wrap' }}>
                {shareLinks.map((link) =>
                  link.copy ? (
                    <Box
                      component="button"
                      key={link.name}
                      type="button"
                      onClick={() => {
                        const done = () => setCopied(true);
                        if (navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(link.href).then(done, done);
                        } else {
                          done();
                        }
                      }}
                      aria-label={`Copy link to: ${title}`}
                      title={copied ? 'Copied!' : 'Copy link'}
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        background: 'none',
                        cursor: 'pointer',
                        borderRadius: 1.5,
                        p: 0.75,
                        color: copied ? 'success.main' : 'text.secondary',
                        display: 'inline-flex',
                        transition: 'color 0.2s ease, border-color 0.2s ease',
                        '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                      }}
                    >
                      <Iconify icon={copied ? 'eva:checkmark-fill' : link.icon} width={16} />
                    </Box>
                  ) : (
                    <Box
                      component="a"
                      key={link.name}
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Share on ${link.name}: ${title}`}
                      title={`Share on ${link.name}`}
                      sx={{
                        border: (theme) => `1px solid ${theme.palette.divider}`,
                        borderRadius: 1.5,
                        p: 0.75,
                        color: 'text.secondary',
                        display: 'inline-flex',
                        transition: 'color 0.2s ease, border-color 0.2s ease',
                        '&:hover': { color: 'primary.main', borderColor: 'primary.main' },
                      }}
                    >
                      <Iconify icon={link.icon} width={16} />
                    </Box>
                  )
                )}
              </Stack>
            )}
          </Stack>
        </Card>
      </MotionInView>
    </Grid>
  );
}

// ----------------------------------------------------------------------
export default function SuccessStories() {
  const dispatch = useDispatch();
  const { successStories, isLoading } = useSelector((state) => state.course);

  // Demo stories, used only when the live endpoint returns nothing
  // (ASAP-FU-021 tracks that endpoint).
  const [fallbackStories, fallbackReady] = useLazyMockWeb(
    'successStories',
    !isLoading && !(Array.isArray(successStories) && successStories.length)
  );

  useEffect(() => {
    dispatch(getSuccessStories());
  }, [dispatch]);

  const stories = Array.isArray(successStories) && successStories.length ? successStories : fallbackStories;

  if (!stories.length && (isLoading || !fallbackReady)) {
    return (
      <SectionShell>
        <Typography variant="subtitle1" sx={{ color: '#000', textAlign: 'center' }}>
          Loading...
        </Typography>
      </SectionShell>
    );
  }

  return (
    <SectionShell>
      <SectionHeader
        eyebrow="Success stories"
        title="Student Success Stories"
        description="Watch our students share their journey to success with ASAP."
      />

      <Grid container spacing={{ xs: 2.5, sm: 3 }} sx={{ alignItems: 'stretch' }}>
        {stories.map((story, index) => (
          <StoryCard key={story.id} story={story} index={index} />
        ))}
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          flexWrap: 'wrap',
          gap: { xs: 1, sm: 1.5 },
          mt: { xs: 3, md: 4 },
        }}
      >
        <Box
          component="a"
          href={`${YOUTUBE_CHANNEL_URL}?sub_confirmation=1`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            py: 1.25,
            borderRadius: 2,
            bgcolor: '#FF0000',
            color: '#FFFFFF',
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
            boxShadow: '0 8px 20px rgba(255, 0, 0, 0.28)',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 12px 26px rgba(255, 0, 0, 0.35)' },
            },
          }}
        >
          <Iconify icon="ri:youtube-fill" width={20} />
          Subscribe
        </Box>

        <Box
          component="a"
          href={YOUTUBE_CHANNEL_URL}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 2.5,
            py: 1.25,
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            color: 'text.primary',
            fontSize: 14,
            fontWeight: 700,
            textDecoration: 'none',
            transition: 'transform 0.3s ease, border-color 0.3s ease, color 0.3s ease',
            '@media (hover: hover)': {
              '&:hover': { transform: 'translateY(-2px)', borderColor: '#DF3E30', color: '#DF3E30' },
            },
          }}
        >
          <Iconify icon="ic:round-video-library" width={20} />
          View more on YouTube
        </Box>
      </Box>
    </SectionShell>
  );
}
